import React, { useRef } from "react";
import cs from "classnames";

export type DeviceModalTab = "settings" | "deviceInformation";

const TABS: { id: DeviceModalTab; label: string }[] = [
  { id: "settings", label: "Settings" },
  { id: "deviceInformation", label: "Device information" },
];

export const getDeviceModalTabId = (tab: DeviceModalTab) =>
  `DeviceModal-tab-${tab}`;

export const getDeviceModalPanelId = (tab: DeviceModalTab) =>
  `DeviceModal-panel-${tab}`;

interface Props {
  activeTab: DeviceModalTab;
  onChange: (tab: DeviceModalTab) => void;
}

export const DeviceModalTabs = ({ activeTab, onChange }: Props) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectAt = (index: number) => {
    onChange(TABS[index].id);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const last = TABS.length - 1;

    switch (event.key) {
      case "ArrowRight":
        selectAt(index === last ? 0 : index + 1);
        break;
      case "ArrowLeft":
        selectAt(index === 0 ? last : index - 1);
        break;
      case "Home":
        selectAt(0);
        break;
      case "End":
        selectAt(last);
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  return (
    <div className="DeviceModalTabs" role="tablist" aria-label="Device">
      {TABS.map((tab, index) => {
        const isActive = tab.id === activeTab;

        return (
          <button
            key={tab.id}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            type="button"
            role="tab"
            id={getDeviceModalTabId(tab.id)}
            aria-selected={isActive}
            aria-controls={getDeviceModalPanelId(tab.id)}
            tabIndex={isActive ? 0 : -1}
            className={cs("DeviceModalTabs__tab", {
              "DeviceModalTabs__tab--active": isActive,
            })}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
